import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Download, FilePlus2, FolderPlus, FolderTree, Shield, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

export const ClientDocumentsPanel = ({ api, clientId, isSuperAdmin }) => {
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/clients/${clientId}/documents`);
      const nextFolders = res.data.folders || [];
      const nextDocuments = res.data.documents || [];
      setFolders(nextFolders);
      setDocuments(nextDocuments);
      if (!selectedFolderId && nextFolders.length > 0) {
        setSelectedFolderId(nextFolders[0].id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [clientId]);

  const selectedFolder = useMemo(() => folders.find((folder) => folder.id === selectedFolderId) || folders[0], [folders, selectedFolderId]);
  const visibleDocuments = useMemo(() => documents.filter((doc) => doc.folder_id === (selectedFolder?.id || '')), [documents, selectedFolder]);

  const downloadDocument = async (document) => {
    try {
      const response = await api.get(`/api/clients/${clientId}/documents/${document.id}/download`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: document.content_type || response.headers['content-type'] }));
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download document');
    }
  };

  const uploadFiles = async (files) => {
    if (!selectedFolder) {
      toast.error('Select a folder first');
      return;
    }
    if (selectedFolder.locked) {
      toast.error('The Contracts folder is system-managed and does not allow manual uploads');
      return;
    }
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append('folder_id', selectedFolder.id);
    Array.from(files).forEach((file) => formData.append('files', file));

    setUploading(true);
    try {
      await api.post(`/api/clients/${clientId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documents uploaded');
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploading(false);
      setDragActive(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const createFolder = async () => {
    const name = window.prompt('Folder name');
    if (!name) return;
    try {
      const res = await api.post(`/api/clients/${clientId}/documents/folders`, { name });
      toast.success('Folder created');
      setSelectedFolderId(res.data.folder.id);
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const deleteDocument = async (document) => {
    setDeletingId(document.id);
    try {
      if (document.source_type === 'contract_book') {
        if (!isSuperAdmin) {
          toast.error('Only super admin can delete signed contracts');
          return;
        }
        const adminPassword = window.prompt('Enter super admin password to delete this signed contract book');
        if (!adminPassword) return;
        await api.delete(`/api/signed-contract-books/${document.contract_book_id}`, { data: { admin_password: adminPassword } });
        toast.success('Signed contract book deleted');
      } else {
        if (!window.confirm(`Delete ${document.display_name || document.original_name}?`)) return;
        await api.delete(`/api/clients/${clientId}/documents/${document.id}`);
        toast.success('Document deleted');
      }
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)] gap-6" data-testid="client-documents-panel">
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#0A1628] flex items-center gap-2"><FolderTree className="w-4 h-4 text-[#014DB7]" /> Folders</h3>
          <Button size="sm" variant="outline" onClick={createFolder} data-testid="create-document-folder-button"><FolderPlus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2">
          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => setSelectedFolderId(folder.id)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${selectedFolder?.id === folder.id ? 'border-[#014DB7] bg-[#014DB7]/5 text-[#014DB7]' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}
              data-testid={`document-folder-${folder.slug}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{folder.name}</span>
                <span className="text-xs text-gray-400">{folder.document_count || 0}</span>
              </div>
              {folder.locked && <p className="text-[11px] text-amber-600 mt-1">System folder</p>}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-[#0A1628]">Documents / {selectedFolder?.name || 'Folder'}</h3>
              <p className="text-sm text-gray-500">
                {selectedFolder?.slug === 'contracts'
                  ? 'Signed contract books are saved here as a whole and cannot be deleted by the client.'
                  : 'Drag and drop files here or browse to upload documents into this folder.'}
              </p>
            </div>
            {!selectedFolder?.locked && (
              <Button size="sm" onClick={() => fileInputRef.current?.click()} data-testid="browse-documents-button"><FilePlus2 className="w-4 h-4 mr-1" /> Browse</Button>
            )}
          </div>

          {!selectedFolder?.locked ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive ? 'border-[#014DB7] bg-[#014DB7]/5' : 'border-gray-200 bg-gray-50'}`}
              data-testid="document-dropzone"
            >
              <UploadCloud className="w-10 h-10 text-[#014DB7] mx-auto mb-3" />
              <p className="font-medium text-[#0A1628]">{uploading ? 'Uploading…' : 'Drop files here'}</p>
              <p className="text-sm text-gray-500 mt-1">PDF, DOCX, XLSX, images, and common office files are supported.</p>
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-3" data-testid="contracts-folder-note">
              <Shield className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-semibold">Contracts folder is protected</p>
                <p>Signed contract books are stored here automatically as a whole record after signing. Clients can view/download, but only super admin can delete.</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {visibleDocuments.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <FilePlus2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>No documents in this folder yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleDocuments.map((document) => (
                <div key={document.id} className="p-4 flex items-center justify-between gap-4" data-testid={`client-document-row-${document.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#0A1628] truncate">{document.display_name || document.original_name}</p>
                      {document.source_type === 'contract_book' && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Signed Contract Book</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {document.source_type === 'contract_book' ? `${document.document_count || 0} signed document(s)` : document.content_type || 'Document'}
                      {' • '}
                      {document.created_at ? new Date(document.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadDocument(document)} data-testid={`download-document-${document.id}`}>
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                    {(document.source_type !== 'contract_book' || isSuperAdmin) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteDocument(document)}
                        disabled={deletingId === document.id}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`delete-document-${document.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> {deletingId === document.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};